import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import QuickMessagesCategoryDialog from "../../components/QuickMessagesCategoryDialog";

const reducer = (state, action) => {
  if (action.type === "LOAD_CATEGORIES") {
    const categories = action.payload;
    const newCategories = [];
    if (isArray(categories)) {
      categories.forEach((category) => {
        const categoryIndex = state.findIndex((u) => u.id === category.id);
        if (categoryIndex !== -1) {
          state[categoryIndex] = category;
        } else {
          newCategories.push(category);
        }
      });
    }
    return [...state, ...newCategories];
  }
  if (action.type === "UPDATE_CATEGORY") {
    const category = action.payload;
    const categoryIndex = state.findIndex((u) => u.id === category.id);
    if (categoryIndex !== -1) {
      state[categoryIndex] = category;
      return [...state];
    } else {
      return [category, ...state];
    }
  }
  if (action.type === "DELETE_CATEGORY") {
    const categoryId = action.payload;
    const categoryIndex = state.findIndex((u) => u.id === categoryId);
    if (categoryIndex !== -1) {
      state.splice(categoryIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const QuickMessagesCategories = () => {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [categories, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCategories();
    }, 200); // Reduzir delay de 500ms para 200ms
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onCategoryEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CATEGORY", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CATEGORY", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickmessage-category`, onCategoryEvent);
    return () => {
      socket.off(`company-${companyId}-quickmessage-category`, onCategoryEvent);
    };
  }, [socket]);

  const fetchCategories = async () => {
    try {
      const companyId = user.companyId;
      const { data } = await api.get("/quick-messages-categories", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CATEGORIES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleOpenCategoryDialog = () => {
    setSelectedCategory(null);
    setCategoryModalOpen(true);
  };

  const handleCloseCategoryDialog = () => {
    setSelectedCategory(null);
    setCategoryModalOpen(false);
    fetchCategories();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await api.delete(`/quick-messages-categories/${categoryId}`);
      toast.success("Categoria deletada com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setDeletingCategory(null);
    setSearchParam("");
    setPageNumber(1);
    fetchCategories();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div style={{ padding: "24px", background: "#f5f7fa", minHeight: "100vh" }}>
      <ConfirmationModal
        title={deletingCategory && `Deletar categoria "${deletingCategory.name}"?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCategory(deletingCategory.id)}
      >
        Todas as respostas rápidas nesta categoria serão mantidas, mas ficarão sem categoria.
      </ConfirmationModal>

      <QuickMessagesCategoryDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchCategories();
        }}
        open={categoryModalOpen}
        onClose={handleCloseCategoryDialog}
        categoryId={selectedCategory && selectedCategory.id}
      />

      {/* Header */}
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
          📁 Categorias de Respostas Rápidas
        </h1>
        <button
          style={{
            padding: "10px",
            background: "var(--primaryColor, #065183)",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.target.style.opacity = "0.9";
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow = "0 2px 8px rgba(6, 81, 131, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = "1";
            e.target.style.transform = "none";
            e.target.style.boxShadow = "none";
          }}
          onClick={handleOpenCategoryDialog}
          title="Adicionar categoria"
        >
          <AddIcon style={{ fontSize: "20px" }} />
        </button>
      </div>

      {/* Search */}
      <div style={{ background: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e8ecf1", marginBottom: "24px", padding: "16px" }}>
        <div style={{ position: "relative" }}>
          <SearchIcon
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
              fontSize: "20px",
            }}
          />
          <input
            type="text"
            placeholder="Buscar categorias..."
            value={searchParam}
            onChange={handleSearch}
            style={{
              width: "100%",
              padding: "10px 12px 10px 40px",
              border: "1px solid #e8ecf1",
              borderRadius: "6px",
              fontSize: "13px",
              fontFamily: "inherit",
              background: "#fafbfc",
              transition: "all 0.2s ease",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#3b82f6";
              e.target.style.background = "white";
              e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e8ecf1";
              e.target.style.background = "#fafbfc";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>
      </div>

      {/* Grid de Categorias */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "20px",
          height: "calc(100vh - 280px)",
          overflowY: "auto",
        }}
        onScroll={handleScroll}
      >
        {categories.map((category) => (
          <div
            key={category.id}
            style={{
              background: "white",
              borderRadius: "12px",
              border: "1px solid #e8ecf1",
              padding: "20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              transition: "all 0.2s ease",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
              e.currentTarget.style.transform = "none";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  background: category.color || "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "white",
                }}
              >
                {category.icon || "📁"}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: "0 0 4px 0", fontSize: "16px", fontWeight: 700, color: "#1a1a1a" }}>
                  {category.name}
                </h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                  {category.description || "Sem descrição"}
                </p>
              </div>
            </div>

            <div style={{ borderTop: "1px solid #e8ecf1", paddingTop: "12px" }}>
              <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#6b7280" }}>
                <strong>{category.quickMessagesCount || 0}</strong> respostas rápidas
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => handleEditCategory(category)}
                style={{
                  background: "none",
                  border: "1px solid #e8ecf1",
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6b7280",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f3f4f6";
                  e.target.style.borderColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "none";
                  e.target.style.borderColor = "#e8ecf1";
                }}
              >
                <EditIcon style={{ fontSize: "16px" }} />
                Editar
              </button>

              <button
                onClick={(e) => {
                  setConfirmModalOpen(true);
                  setDeletingCategory(category);
                }}
                style={{
                  background: "none",
                  border: "1px solid #fee2e2",
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#ef4444",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#fee2e2";
                  e.target.style.borderColor = "#fca5a5";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "none";
                  e.target.style.borderColor = "#fee2e2";
                }}
              >
                <DeleteOutlineIcon style={{ fontSize: "16px" }} />
                Deletar
              </button>
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ gridColumn: "1 / -1", padding: "40px", textAlign: "center", color: "#999" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "3px solid #e8ecf1",
                  borderTop: "3px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              Carregando...
            </div>
          </div>
        )}

        {!loading && categories.length === 0 && (
          <div style={{ gridColumn: "1 / -1", padding: "40px", textAlign: "center", color: "#999" }}>
            Nenhuma categoria encontrada
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default QuickMessagesCategories;
